import citraAgenticAiImage from "../assets/images/products/citra-agentic-ai-generated.png";
import citraAccessImage from "../assets/images/products/citra-access.svg";

export const getProductThumbnail = (product) => {
  const productText = [
    product?.title,
    product?.summary,
    ...(product?.categories || []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  if (productText.includes("agentic") && productText.includes("ai")) {
    return citraAgenticAiImage;
  }

  return product.thumbnail_url;
};

export const citraAccessProduct = {
  slug: "citra-access",
  title: "Citra Access",
  subtitle: "Secure External User Provisioning for SAP",
  description:
    "Govern, approve and provision external users across SAP Cloud Identity Services, SAP Build Work Zone and SAP applications.",
  summary:
    "Onboard customers, contractors, suppliers and partners through an approval-driven workflow that provisions access only after authorisation has been granted.",
  promise: "Secure. Scalable. Auditable.",
  image: citraAccessImage,
  approvalIntro: [
    "Most external user onboarding processes focus on creating identities.",
    "Citra Access focuses on governing access.",
    "Unlike traditional self-registration approaches, Citra Access ensures users are not provisioned into SAP Cloud Identity Services until all required approvals have been completed.",
    "This gives organisations complete control over who receives access, what access they receive, and why.",
  ],
  pillars: [
    {
      title: "Secure",
      description:
        "Prevent unauthorised access through approval-first onboarding.",
      points: [
        "Approval before provisioning",
        "Role-based access controls",
        "Least privilege security model",
        "Integration with enterprise identity providers",
        "Automated group and role assignment",
      ],
    },
    {
      title: "Scalable",
      description:
        "Support growing user communities without increasing administration effort.",
      points: [
        "Automated onboarding",
        "Cloud-native architecture",
        "High-volume processing",
        "Multi-customer support",
        "Multi-contract support",
      ],
    },
    {
      title: "Auditable",
      description: "Create a complete record of every access decision.",
      points: [
        "Approval history",
        "Provisioning history",
        "User lifecycle tracking",
        "Compliance reporting",
        "Full audit trail",
      ],
    },
  ],
  steps: [
    {
      title: "Registration",
      description: "User submits a registration request.",
    },
    {
      title: "Verification",
      description: "Email ownership is verified.",
    },
    {
      title: "Approval",
      description: "Request is routed to the correct business approver.",
    },
    {
      title: "Provisioning",
      description:
        "User is created in SAP Cloud Identity Services and assigned the correct permissions.",
    },
    {
      title: "Access Granted",
      description: "User receives access to the exact SAP applications approved.",
    },
  ],
  sapFit: [
    {
      title: "Identity Services",
      points: [
        "SAP Cloud Identity Services",
        "SAP Identity Authentication",
        "SAP Identity Provisioning",
      ],
    },
    {
      title: "SAP Experience Layer",
      points: ["SAP Build Work Zone", "SAP Build Apps"],
    },
    {
      title: "SAP Business Technology Platform",
      points: [
        "CAP Applications",
        "UI5 Applications",
        "SAP BTP Services",
      ],
    },
    {
      title: "SAP Business Systems",
      points: ["SAP S/4HANA", "SAP ECC", "Custom SAP Applications"],
    },
  ],
  comparison: [
    {
      capability: "Email Verification",
      standard: "Yes",
      citraAccess: "Yes",
    },
    {
      capability: "Business Approval",
      standard: "No",
      citraAccess: "Yes",
    },
    {
      capability: "Approval Before User Creation",
      standard: "No",
      citraAccess: "Yes",
    },
    {
      capability: "Automated Role Assignment",
      standard: "Limited",
      citraAccess: "Yes",
    },
    {
      capability: "SAP Work Zone Provisioning",
      standard: "Manual",
      citraAccess: "Yes",
    },
    {
      capability: "Audit Trail",
      standard: "Limited",
      citraAccess: "Yes",
    },
    {
      capability: "Multi-Level Approvals",
      standard: "No",
      citraAccess: "Yes",
    },
    {
      capability: "Governance Controls",
      standard: "No",
      citraAccess: "Yes",
    },
  ],
  contact: {
    enquiry: "citra-access-demo",
    title: "Request A Citra Access Demo",
    description:
      "See how Citra Access can help your organisation securely onboard customers, contractors, suppliers and partners across SAP landscapes.",
    messagePlaceholder:
      "Tell us about your external user onboarding, approval, or SAP provisioning requirements.",
    submitLabel: "Request Demo",
  },
};

export const productDetails = [citraAccessProduct];

export const getProductBySlug = (slug) =>
  productDetails.find((product) => product.slug === slug);
